var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classNames from 'classnames';
import { useLink } from "../../hooks/useLink";
import { makeStyles } from "../../styles/index";
import { useTheme } from "../../hooks/useTheme";
/** squareかdialogの時に適用されるスタイル */
var squareOrDialogStyle = function (theme) { return ({
    border: 'solid currentColor',
    borderWidth: theme.shape.borderWidth.normal,
}); };
/** squareかdialogでライトテーマ/塗りつぶしなしの時に適用されるスタイル */
var squareOrDialogOutlinedLightStyle = function (theme) { return ({
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.light.background.paper,
    borderColor: theme.palette.primary.main,
    '&:disabled': {
        color: theme.palette.light.text.disabled,
        borderColor: theme.palette.light.action.disabled,
    },
    '&:hover:not(:disabled)': {
        backgroundColor: theme.palette.primary['50'],
    },
    '&:active': {
        color: theme.palette.dark.text.primary,
        backgroundColor: theme.palette.primary.main,
    },
}); };
/** squareかdialogでライトテーマ/塗りつぶしありの時に適用されるスタイル */
var squareOrDialogFillLightStyle = function (theme) { return ({
    color: theme.palette.dark.text.primary,
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:disabled': {
        color: theme.palette.dark.text.primary,
        backgroundColor: theme.palette.light.action.disabled,
        borderColor: theme.palette.light.action.disabled,
    },
    '&:hover:not(:disabled)': {
        backgroundColor: theme.palette.primary.light,
        borderColor: theme.palette.primary.light,
    },
    '&:active': {
        backgroundColor: theme.palette.primary.dark,
        borderColor: theme.palette.primary.dark,
    },
}); };
/** textのライトテーマの時に適用されるスタイル */
var textLightStyle = function (theme) { return ({
    color: theme.palette.dark.background.paper,
    backgroundColor: theme.palette.light.background.paper,
    '&:disabled': {
        color: theme.palette.light.text.disabled,
        backgroundColor: theme.palette.light.background.paper,
    },
    '&:hover:not(:disabled)': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary['50'],
    },
    '&:active': {
        color: theme.palette.dark.text.primary,
        backgroundColor: theme.palette.primary.main,
    },
}); };
/** ダークテーマ時に適用されるスタイル */
var darkStyle = function (theme) { return ({
    color: theme.palette.dark.text.primary,
    backgroundColor: 'transparent',
    borderColor: 'currentColor',
    '&:disabled': {
        color: theme.palette.dark.text.disabled,
        borderColor: theme.palette.dark.action.disabled,
    },
    '&:hover:not(:disabled)': {
        borderColor: theme.palette.primary.light,
        color: theme.palette.primary.light,
    },
    '&:active': {
        color: theme.palette.dark.text.primary,
        backgroundColor: theme.palette.primary.light,
    },
}); };
// TODO 固定値は仮
var styles = function (theme) { return ({
    root: {
        // text想定
        height: 48,
        minWidth: 130,
        fontSize: theme.typography.body1.fontSize,
        fontWeight: theme.typography.body1.fontWeight,
        outline: 0,
        position: 'relative',
        textDecoration: 'none',
        transition: '0.2s',
        userSelect: 'none',
        cursor: 'pointer',
        padding: theme.spacing(0, 3),
        // SVGが子の最初にあるときは左のpaddingを16px減らす
        '& > $content > svg:first-child': {
            marginLeft: theme.spacing(-2),
        },
        // SVGが子の最後にあるときは右のpaddingを16px減らす
        '& > $content > svg:last-child': {
            marginRight: theme.spacing(-2),
        },
        '&:disabled': {
            pointerEvents: 'none',
        },
    },
    content: {
        height: '100%',
        display: 'flex',
        flex: '0 0 auto',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
    },
    text: {
        borderWidth: 0,
        '&$light': __assign({}, textLightStyle(theme)),
        '&$dark': __assign({}, darkStyle(theme)),
    },
    square: __assign(__assign({}, squareOrDialogStyle(theme)), { height: 40, borderRadius: 8, padding: theme.spacing(0, 2), '&$light': __assign({}, squareOrDialogOutlinedLightStyle(theme)), '&$light$fill': __assign({}, squareOrDialogFillLightStyle(theme)), '&$dark': __assign({}, darkStyle(theme)) }),
    dialog: __assign(__assign({}, squareOrDialogStyle(theme)), { height: 60, borderRadius: 36, '&$light': __assign({}, squareOrDialogOutlinedLightStyle(theme)), '&$light$fill': __assign({}, squareOrDialogFillLightStyle(theme)), '&$dark': __assign({}, darkStyle(theme)) }),
    dark: {},
    light: {},
    fill: {},
    outlined: {},
}); };
var useStyles = makeStyles(styles, { name: 'SnButton' });
export var Button = React.forwardRef(function (props, ref) {
    var _a;
    var _b = props.variant, variant = _b === void 0 ? 'text' : _b, dark = props.dark, light = props.light, fill = props.fill, children = props.children, classNameProp = props.className, classesProp = props.classes, others = __rest(props, ["variant", "dark", "light", "fill", "children", "className", "classes"]);
    var classes = useStyles({ classes: classesProp });
    var theme = useTheme({ dark: dark, light: light });
    var Component = useLink(props, 'button');
    var className = classNames(classes.root, classes[theme.type], classes[variant], (_a = {},
        _a[classes.fill] = variant !== 'text' && fill,
        _a[classes.outlined] = variant !== 'text' && !fill,
        _a), classNameProp);
    return (React.createElement(Component, __assign({ ref: ref, className: className }, others),
        React.createElement("div", { className: classes.content }, children)));
});
